import {
  awsRdsVpcIdOptionProvider,
  dbAwsRdsIamWriteCommands,
  gcpCloudSqlVpcIdOptionProvider,
} from "@p0security/integrations-db-frontend";
import {
  installSql,
  installTf,
} from "@p0security/integrations-mysql-shared/iac";
import type { MysqlIamWriteConfig } from "@p0security/integrations-mysql-shared/types";
import { Typography } from "@p0security/ui";

import type { MysqlInstaller } from "./types.ts";

type MysqlIamWriteInstructions = NonNullable<
  MysqlInstaller["components"]["iam-write"]["instructions"]
>;

type MysqlHostedIamWriteInstructions<
  Hosting extends MysqlIamWriteConfig["hosting"],
> = (
  context: Parameters<MysqlIamWriteInstructions>[0],
  id: Parameters<MysqlIamWriteInstructions>[1],
  item: Parameters<MysqlIamWriteInstructions>[2] & {
    hosting: Hosting;
  }
) => ReturnType<MysqlIamWriteInstructions>;

const gcpCloudSqlIamWriteInstructions: MysqlHostedIamWriteInstructions<
  Extract<MysqlIamWriteConfig["hosting"], { type: "gcp-cloudsql" }>
> = (_context, _id, item) => {
  // MySQL usernames are capped at 32 characters and cannot contain "@", so
  // Cloud SQL derives the login name from the service account email by
  // dropping the "@project-id.iam.gserviceaccount.com" domain entirely
  const dbUser =
    item.hosting.connectorServiceAccount?.replace(
      /@.*\.gserviceaccount\.com$/,
      ""
    ) ?? "";
  return {
    help: (
      <>
        Follow the below instructions in order to install P0 on this CloudSQL
        instance.
      </>
    ),
    commands: {
      iac: [
        {
          // Note: for MySQL, the name parameter for this block should be the entire
          // service account email: https://docs.cloud.google.com/sql/docs/mysql/add-manage-iam-users#terraform_2
          command: `
resource "google_sql_user" "iam_service_account" {
  project  = "${item.hosting.projectId}"
  instance = "${item.hosting.instanceName}"
  type     = "CLOUD_IAM_SERVICE_ACCOUNT"
  name     = "${item.hosting.connectorServiceAccount}"
}
          `,
          header: (
            <div>
              <Typography.Paragraph>
                Apply the following terraform in order to provision the P0
                connector user.
              </Typography.Paragraph>
            </div>
          ),
        },
        {
          command: `GRANT cloudsqlsuperuser TO '${dbUser}'@'%';`,
          header: (
            <>
              <div>
                <Typography.Paragraph>
                  Then, run the following SQL statement to grant P0
                  administrative access.
                </Typography.Paragraph>
              </div>
            </>
          ),
        },
      ],
    },
  };
};

const awsRdsIamWriteInstructions: MysqlHostedIamWriteInstructions<
  Extract<MysqlIamWriteConfig["hosting"], { type: "aws-rds" }>
> = (context, id, item) => {
  return {
    help: (
      <>
        To install P0 on this account, provision P0&apos;s access using
        Terraform:
      </>
    ),
    commands: dbAwsRdsIamWriteCommands(
      context,
      id,
      item
    )({
      sqlInstructions: [
        {
          command: installSql,
          header: (
            <div>
              <Typography.Paragraph>
                First, configure the P0 connector&apos;s MySQL user.
              </Typography.Paragraph>
              <Typography.Paragraph>
                Run these SQL statements in the database:
              </Typography.Paragraph>
            </div>
          ),
        },
      ],
      installTf,
    }),
  };
};

export const mysqlInstaller: MysqlInstaller = {
  components: {
    "iam-write": {
      items: {
        hosting: {
          options: {
            "aws-rds": {
              items: {
                vpcId: {
                  optionProvider: awsRdsVpcIdOptionProvider,
                },
              },
            },
            "gcp-cloudsql": {
              items: {
                vpcId: {
                  optionProvider: gcpCloudSqlVpcIdOptionProvider,
                },
              },
            },
          },
        },
      },
      instructions: (context, id, item) => {
        const { hosting } = item;
        return hosting.type === "gcp-cloudsql"
          ? gcpCloudSqlIamWriteInstructions(context, id, { ...item, hosting })
          : awsRdsIamWriteInstructions(context, id, { ...item, hosting });
      },
    },
  },
};
